import type React from 'react';
import { faChartLine, faSimCard } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from '../../../../utils/classNames';
import {
    DataWarningsQuickView,
    DeviceStatusQuickView,
    RecentExceedancesQuickView,
} from './SummaryQuickViews/SummaryQuickViews';
import { DEVICE_STATUS_DOT_CLASSES, StatusDot } from './StatusDot/StatusDot';
import { SummaryPopover } from './SummaryPopover/SummaryPopover';
import { getUsageSeverityClasses } from './UsageProgressBar/UsageProgressBar';
import { buildEmptyProjectSummary, type ProjectRowSummary } from '../graphql/projectSummary';

const SUMMARY_METRIC_GROUP_CLASSES = 'inline-flex items-center gap-2';

function stopRowToggle(event: React.SyntheticEvent) {
    event.stopPropagation();
}

function SummaryPopoverTrigger({
    trigger,
    panel,
    ariaLabel,
}: {
    trigger: React.ReactNode;
    panel: React.ReactNode;
    ariaLabel: string;
}) {
    return (
        <SummaryPopover panel={panel}>
            <button
                type="button"
                className="focus-visible:ring-notification-info/40 rounded-md focus-visible:outline-none
                    focus-visible:ring-2"
                aria-label={ariaLabel}
                onClick={stopRowToggle}
                onMouseDown={stopRowToggle}
            >
                {trigger}
            </button>
        </SummaryPopover>
    );
}

function ProjectSummaryCount({ count, marker }: { count: number; marker: React.ReactNode }) {
    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
                {marker}
            </span>
            {/* Room for two digits keeps the markers aligned across rows. */}
            <span className="min-w-[2ch] text-xs font-medium tabular-nums text-gray-700">
                {count}
            </span>
        </span>
    );
}

function AlertIconBadge({ icon, className }: { icon: React.ReactNode; className: string }) {
    return (
        <span className={classNames('inline-flex text-base leading-none', className)} aria-hidden>
            {icon}
        </span>
    );
}

export function ProjectDeviceStatusCell({
    summary,
    total,
}: {
    summary: ProjectRowSummary | null;
    total: number;
}): React.ReactElement {
    const online = summary?.online ?? 0;
    const offline = summary?.offline ?? 0;
    const inactive = summary?.inactive ?? 0;

    return (
        <SummaryPopoverTrigger
            ariaLabel={gettext('STATUS')}
            trigger={
                <span className={SUMMARY_METRIC_GROUP_CLASSES}>
                    <ProjectSummaryCount
                        count={online}
                        marker={<StatusDot colorClassName={DEVICE_STATUS_DOT_CLASSES.online} />}
                    />
                    <ProjectSummaryCount
                        count={offline}
                        marker={<StatusDot colorClassName={DEVICE_STATUS_DOT_CLASSES.offline} />}
                    />
                    <ProjectSummaryCount
                        count={inactive}
                        marker={<StatusDot colorClassName={DEVICE_STATUS_DOT_CLASSES.inactive} />}
                    />
                </span>
            }
            panel={
                <DeviceStatusQuickView
                    online={online}
                    offline={offline}
                    inactive={inactive}
                    total={total}
                />
            }
        />
    );
}

export function ProjectAlertsCell({
    summary,
}: {
    summary: ProjectRowSummary | null;
}): React.ReactElement {
    const { recentExceedances, dataWarnings } = summary ?? buildEmptyProjectSummary();
    const hasReachedDataLimit = dataWarnings.devices.some((device) => device.isLimit);

    return (
        <div className={SUMMARY_METRIC_GROUP_CLASSES}>
            <SummaryPopoverTrigger
                ariaLabel={gettext('RECENT_EXCEEDANCES')}
                trigger={
                    <ProjectSummaryCount
                        count={recentExceedances.count}
                        marker={
                            <AlertIconBadge
                                icon={<FontAwesomeIcon icon={faChartLine} />}
                                className="text-warning"
                            />
                        }
                    />
                }
                panel={<RecentExceedancesQuickView events={recentExceedances.events} />}
            />
            <SummaryPopoverTrigger
                ariaLabel={gettext('DATA_WARNINGS')}
                trigger={
                    <ProjectSummaryCount
                        count={dataWarnings.count}
                        marker={
                            <AlertIconBadge
                                icon={<FontAwesomeIcon icon={faSimCard} />}
                                className={getUsageSeverityClasses(hasReachedDataLimit).text}
                            />
                        }
                    />
                }
                panel={<DataWarningsQuickView devices={dataWarnings.devices} />}
            />
        </div>
    );
}
